import {
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ItemPicker,
	CommonItemPickerOptions,
	ItemPickerOptions,
	SharePointItemPickerOptions,
	WebDavItemPickerOptions,
	ItemPickerLocation,
	Selection,
	FileSelection,
	ItemPickerMode
} from "../ItemPicker/ItemPicker";
import { isNil, isNotNil } from "client/Misc/utils";
import { DocumentStoreType } from "client/Api/Documents/common";
import { never } from "client_legacy/ndaw/errors";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { GridItem } from "../SharePoint/SharePointOverView";

type Options = {
	sharePointOptions: SharePointItemPickerOptions;
	webDavOptions: WebDavItemPickerOptions;
	commonOptions?: CommonItemPickerOptions;
};
export type SelectFilesDialogProps = {
	options: Options;
	initialLocation?: ItemPickerLocation;
	onClose: () => void;
	onBeforeSelection?: (selection: FileSelection) => Promise<boolean>;
	onSelectionDone: (selection: FileSelection) => void;
};

export const SelectFilesDialog = (props: SelectFilesDialogProps): ReactElement => {
	const {
		options,
		initialLocation,
		onBeforeSelection,
		onSelectionDone,
		onClose
	} = props;

	const [selectedItems, setSelectedItems] = useState<Selection | undefined>(undefined);
	const [location, setLocation] = useState<ItemPickerLocation | undefined>(initialLocation);

	const alertManager: AlertManager = useAlerts();

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const isButtonDisabled: boolean = isNil(selectedItems) || selectedItems.items.length === 0;

	const itemPickerOptions: ItemPickerOptions = useMemo(
		() => {
			const commonOptions: CommonItemPickerOptions = options.commonOptions
				? options.commonOptions
				: {
					mode: ItemPickerMode.MultipleFilesOrOneNoteNotebooks,
					creationOptions: {
						folder: true
					}
				};

			return {
				sharePointOptions: options.sharePointOptions,
				webDavOptions: options.webDavOptions,
				commonOptions
			};
		},
		[options]
	);

	const insertFilesHandler = useCallback(
		async (): Promise<void> => {
			if (isNotNil(selectedItems) && selectedItems.items.length > 0) {
				let fileSelection: FileSelection;

				switch (selectedItems.kind) {
					case DocumentStoreType.SharePoint:
						if (doesContainInfectedItems(selectedItems.items)){
							alertManager.showError(
								localize("_ThisFileIsInfectedByMalwareToProtectYourPcYouCannotOpenShareOrDownloadThisFile")
							);

							return;
						}
						const failedFileNames: string[] = selectedItems.items
							.filter(item => item.model.permissions && !item.model.permissions.canDownload)
							.map(item => item.name);

						if (failedFileNames.length > 0) {
							alertManager.showError(
								<FormattedText
									text={localize("_NoPermissionForTheseFiles")}
									thingsToReplace={[
										{ what: "{0}", filler: failedFileNames.join(", ") }
									]}
								/>
							);
							return;
						}

						fileSelection = {
							items: selectedItems.items.map(x => {
								if (x.model.itemType === DocumentItemType.Folder) {
									throw new Error("Cannot select folder");
								}
								return x.model;
							}),
							kind:"sharepoint"
						};
						break;
					case DocumentStoreType.WebDav:
						fileSelection={
							items: selectedItems.items.map(x => {
								if (x.model.isFolder) {
									throw new Error("Cannot select folder");
								}

								if (location?.kind !== DocumentStoreType.WebDav) {
									throw new Error("Unexpected Error during webDav files selections");
								}

								return {
									...x.model,
									serverId: location.folder
										.server
										.id
								};
							}),
							kind: "webdav"
						};
						break;
					default:
						never(selectedItems);
				}

				const response: boolean = onBeforeSelection
					? await onBeforeSelection(fileSelection)
					: true;

				if (response) {
					onSelectionDone(fileSelection);
					onClose();
				}
			}
		},
		[
			alertManager,
			location,
			onBeforeSelection,
			onClose,
			onSelectionDone,
			selectedItems
		]
	);

	return (
		<Dialog
			variant="left-slider"
			onClose={onClose}
			trackingId="open-files-dialog"
		>
			<Dialog.Header
				title={localize("_SelectFile")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<ItemPicker
					options={itemPickerOptions}
					location={location}
					onLocationChange={setLocation}
					selectedItems={selectedItems}
					onItemsSelected={setSelectedItems}
					shouldOpenFileOnClick={!isMobile}
				/>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={insertFilesHandler}
						text={localize("_InsertFile")}
						isDisabled={isButtonDisabled}
						trackingId="done"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

SelectFilesDialog.displayName="SelectFilesDialog";

function doesContainInfectedItems(items: readonly GridItem[]): boolean {
	for (const item of items) {
		if (item.model.itemType === DocumentItemType.File && item.isInfected) {
			return true;
		}
	}
	return false;
}

type SelectFilesDialogInitialData = Omit<SelectFilesDialogProps, "onClose" | "onSelectionDone">;

export function useSelectFilesDialog(
): [
	element: ReactElement | null,
	open: (props: SelectFilesDialogInitialData) => Promise<TryResult<FileSelection>>
] {
	return useDataInputDialog<SelectFilesDialogInitialData, FileSelection>(
		(props, onClose, onSaved) => (
			<SelectFilesDialog
				{...props}
				onClose={onClose}
				onSelectionDone={onSaved}
			/>
		)
	);
}